import React, {useEffect, useRef, useState} from 'react';
import { Template } from "../../../components/template/template.jsx";
import "../../../css/style-report.css";
import "../../../css/input-date-time.css";
import ReportUtils from "../../../utils/report-utils.js";
import {useGlobalKey} from "../../../hooks/use-global-key.js";
import {Card} from "../../../components/card/card.jsx";
import { subMonths} from "date-fns";
import HeaderButtons from "../../../components/reports/report-menu/header-buttons.jsx";
import NotAppliedContent from "../../../components/reports/report-menu/not-applied-content.jsx";
import NoDataContent from "../../../components/reports/report-menu/no-data-content.jsx";
import Menu from "../../../components/reports/report-menu/menu.jsx";
import Filters from "./components/filters.jsx";
import Content from "./components/content.jsx";
import {useMutation, useQuery} from "@tanstack/react-query";
import equipmentsService from "../../../services/web/equipments-service.js";
import reportUtils from "../../../utils/report-utils.js";
import AutoDatetime from "../../../components/reports/auto-datetime/auto-datetime.jsx";
import fixedPostInspectionService from "../../../services/reports/fixed-post-inspection-service.js";
import SubScroll from "../../../components/sub-scroll/sub-scroll.jsx";
import projectUtils from "../../../utils/project-utils.js";

const initialFilters = {
  dtStart: subMonths(new Date(),1),
  dtEnd: new Date(),
  userName: [],
  userCpf: [],
  userLogin: [],
  places: [],
};

const filterLabels = {
  dtStart: "Data/hora inicial",
  dtEnd: "Data/hora final",
  userName: "Nome",
  userCpf: "CPF",
  userLogin: "Login",
  places: "Local pré-cadastrado",
};

const initialTableColumns = {
  name: {label: "Local", visible: true},
  dt_create: {label: "Data/hora", visible: true},
  namewar: {label: "Agente", visible: true},
  observations: {label: "Observações", visible: true}
};

const FixedPostInspection = () => {
  const [loading, setLoading] = useState(false);

  const [collapsed, setCollapsed] = useState(false);
  const buttonRef = useRef(null);
  const [fixedTop, setFixedTop] = useState(null);

  const [filters, setFilters] = useState(initialFilters);
  const [reportData, setReportData] = useState(null);

  const [details, setDetails] = useState(false);

  const [title] = useState("Vistoria de posto fixo");
  const [headerSelected, setHeaderSelected] = useState(-1);
  const [tableColumns, setTableColumns] = useState(initialTableColumns);
  const printComponentRef = useRef(null);

  const {data: equipments = [], isLoading: isLoadingEquipments} = useQuery({
    queryFn: equipmentsService.allEquipments,
    select: data => data.map(equipment => ({label: equipment.text, value: equipment.id})),
    queryKey: ['equipments']
  })

  const mutation = useMutation({
    mutationFn: (payload) => fixedPostInspectionService.apply(payload),
    onSuccess: data => setReportData(data),
  })

  const apply = async () => {
    ReportUtils.applyLoad({
      setLoading,
      clearContent: () => {
        setReportData(null)
      }
    });

    const validation = reportUtils.periodValidate(filters.dtStart, filters.dtEnd);

    if(!validation.ok){
      projectUtils.swalErrorMessage("", validation.message);
      setLoading(false);
      return;
    }

    try {
      setLoading(true)
      const formattedFilters = {
        dtStart: filters.dtStart ? ReportUtils.formatDateTime(filters.dtStart) : null,
        dtEnd: filters.dtEnd ? ReportUtils.formatDateTime(filters.dtEnd) : null,
      };

      const auditFilters = ReportUtils.filters({
          ...filters,
          dtStart: formattedFilters.dtStart,
          dtEnd: formattedFilters.dtEnd,
        },
        filterLabels, true);

      mutation.mutate({
        ...buildPayload(filters),
        filters: auditFilters
      });

      setDetails(filters.searchDetails);

      const scale = window.visualViewport?.scale ?? 1;
      if (window.innerWidth <= 1440 || scale > 1) setCollapsed(true);

    } catch (error) {

      ReportUtils.alertErrorMessage("Erro ao gerar o relatório.");
      console.error("Erro ao gerar o relatório", error);

    } finally {
      setLoading(false);
    }
  };

  const clearSearch = () => {
    const refDate = new Date;
    setFilters({...initialFilters,
                      dtStart: subMonths(refDate, 1),
                      dtEnd: refDate});
    setReportData(null);
  }

  useEffect(() => {
    if (!reportData) return;

    ReportUtils.appendFilters(
      ReportUtils.filters({
        ...filters,
        dtStart: filters.dtStart ? ReportUtils.formatDateTime(filters.dtStart) : "",
        dtEnd: filters.dtEnd ? ReportUtils.formatDateTime(filters.dtEnd) : ""
      }, filterLabels)
    );

  }, [reportData]);

  const toggleFilters = () => {
    if (!collapsed && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setFixedTop(rect.top);
    }

    setCollapsed(prev => !prev);
  }

  useGlobalKey("Enter", apply);
  useGlobalKey("Escape", clearSearch);

  function buildPayload(data) {
    const payloadObj = {
      dtStart: reportUtils.formatDateTime(data.dtStart),
      dtEnd: reportUtils.formatDateTime(data.dtEnd),
    }

    if(data.userName.length > 0) payloadObj.userName = data.userName
    if(data.userLogin.length > 0) payloadObj.userLogin = data.userLogin
    if(data.userCpf.length > 0) payloadObj.userCpf = data.userCpf
    if(data.places.length > 0) payloadObj.places = data.places

    return payloadObj;
  }

  return (
    <SubScroll dependency={[collapsed, reportData]}>
      <div className={"flex"}>

        <Template isLoading={loading || isLoadingEquipments || mutation.isPending}>
          <Template.Header>
            <div className="flex justify-between w-full">
              <Template.Title>{title}</Template.Title>
              <HeaderButtons
                exportable={true}
                exportInSheets={true}
                printable={true}
                headerChange={true}
                visibilityColumns={true}
                columns={tableColumns}
                setColumns={setTableColumns}
                printComponentRef={printComponentRef}
                reportData={reportData}
                data={reportData?.length}
                headerSelected={headerSelected}
                setHeaderSelected={setHeaderSelected}
              />
            </div>
          </Template.Header>
          <Template.Body className={`${collapsed ? 'p-[0]' : ''}`}>
            <Menu buttonRef={buttonRef} toggleFilters={toggleFilters} fixedTop={fixedTop}
                  collapsed={collapsed} apply={apply} clearSearch={clearSearch}>

              <Filters setFilters={setFilters} filters={filters} equipments={equipments}/>
              <AutoDatetime setFilters={setFilters}/>

            </Menu>
          </Template.Body>
        </Template>

        <Card className={`w-full h-fit mr-2 ${!reportData || reportData?.length === 0 ? 'mt-[59px]' : 'mt-[61px]' } print:!mt-[-15px] transform ${!reportData || reportData?.length === 0 ? '!bg-[#f3f3f4] !border-none !-translate-y-[5px]' : ''} ${collapsed ? 'ml-2' : ''}`}>
          <Card.Body>
            <div className={`flex-1 ${!reportData || reportData?.length === 0 ? 'p-[0]' : 'px-[20px] py-[5px]'}`}>
              <NotAppliedContent reportData={reportData}/>
              <NoDataContent reportData={reportData} data={reportData?.length}/>
              <Content reportData={reportData} data={reportData?.length} entity={headerSelected} title={title} collapsed={collapsed} setLoading={setLoading} printComponentRef={printComponentRef} details={details} tableColumns={tableColumns}/>
            </div>
          </Card.Body>
        </Card>

      </div>
    </SubScroll>
  );
};

export default FixedPostInspection;